import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, Clock, ShieldAlert, Mic2, MessageSquare, Image, ArrowLeft } from "lucide-react";

const TEILS = [
  {
    n: "Teil 1",
    title: "Sich vorstellen",
    desc: "Introduce yourself (name, country, job, languages…), spell a word, and say a number.",
    Icon: Mic2,
  },
  {
    n: "Teil 2",
    title: "Um Informationen bitten",
    desc: "Ask and answer questions on a everyday theme using keyword cards.",
    Icon: MessageSquare,
  },
  {
    n: "Teil 3",
    title: "Bitten formulieren",
    desc: "Make and respond to polite requests (with Sie) from picture cards.",
    Icon: Image,
  },
];

const RULES = [
  { Icon: Clock, text: "Timed, ~15 minutes — like the real exam." },
  { Icon: ShieldAlert, text: "Work through all three parts in one go — your full scored feedback comes at the end." },
  { Icon: GraduationCap, text: "Scored at the end. Pass mark is 60% (pass / fail, like Goethe)." },
];

/** Mock Test briefing — explains the A1 Sprechen exam, then launches a scored mock_test session. */
const MockTest = () => {
  const navigate = useNavigate();

  const begin = () => {
    localStorage.setItem("aura_mode", "mock_test");
    navigate("/dashboard/session");
  };

  return (
    <div className="relative min-h-screen bg-base text-ink overflow-hidden" data-testid="page-mock-test">
      <div aria-hidden className="pointer-events-none fixed -top-40 left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] rounded-full bg-aura-cyan/8 blur-[160px]" />

      <div className="relative max-w-2xl mx-auto px-6 py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-[12px] text-whisper hover:text-ink" data-testid="mock-back">
          <ArrowLeft size={14} /> Dashboard
        </Link>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="mt-6 section-label">Mock Test</div>
          <h1 className="mt-2 font-display text-4xl font-semibold">Goethe A1 · Sprechen</h1>
          <p className="mt-3 text-whisper text-[15px]">
            A full speaking exam under real conditions — three parts, timed, then a score. No hints. Ready when you are.
          </p>

          <div className="mt-7 space-y-3">
            {RULES.map(({ Icon, text }, i) => (
              <div key={i} className="flex items-start gap-3 glass rounded-2xl p-4 hairline">
                <div className="w-9 h-9 rounded-xl border border-aura-cyan/25 bg-aura-cyan/5 grid place-items-center text-aura-cyan shrink-0">
                  <Icon size={16} strokeWidth={1.5} />
                </div>
                <span className="text-[13.5px] text-ink/90">{text}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 section-label">The three parts</div>
          <div className="mt-3 grid sm:grid-cols-3 gap-3">
            {TEILS.map(({ n, title, desc, Icon }) => (
              <div key={n} className="glass rounded-2xl p-4 hairline">
                <div className="flex items-center gap-2 text-aura-cyan">
                  <Icon size={15} strokeWidth={1.5} />
                  <span className="font-mono text-[10px] tracking-[0.18em] uppercase">{n}</span>
                </div>
                <div className="mt-2 font-display text-[15px] font-medium">{title}</div>
                <div className="mt-1 text-[12px] text-whisper">{desc}</div>
              </div>
            ))}
          </div>

          <button onClick={begin} className="btn-primary w-full justify-center text-[13px] mt-9" data-testid="mock-begin">
            Begin Mock Test <GraduationCap size={15} />
          </button>
          <p className="mt-3 text-center text-[11px] text-whisper">
            AURA connects to your microphone and starts immediately.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default MockTest;
